import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { db } from '../firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const { login, currentUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (currentUser) {
      navigate('/');
    }
  }, [currentUser, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const isEmail = username.includes('@');
      let targetEmail = '';
      let targetUserDoc = null;

      const usersRef = collection(db, "users");
      const q = isEmail 
        ? query(usersRef, where("email", "==", username))
        : query(usersRef, where("username", "==", username));

      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        setError('User not found.');
        setLoading(false);
        return;
      }

      targetUserDoc = snapshot.docs[0].data();
      targetEmail = targetUserDoc.email;

      // Check status
      if (targetUserDoc.disabled || targetUserDoc.status === 'Disabled') {
        setError('Your account has been disabled. Please contact an administrator.');
        setLoading(false);
        return;
      }

      if (targetUserDoc.status === 'Inactive') {
        setError('Your account is inactive.');
        setLoading(false);
        return;
      }

      if (!targetEmail) {
        setError('This user does not have a valid email configured for login.');
        setLoading(false);
        return;
      }

      await login(targetEmail, password);
      
    } catch (err) {
      console.error(err);
      if (err.code === 'auth/invalid-credential' || err.code === 'auth/wrong-password') {
        setError('Invalid password.');
      } else {
        setError('Failed to sign in. Please try again later.');
      }
    }
    setLoading(false);
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-bg-primary px-4">
      <div className="card w-full max-w-md p-8 relative overflow-hidden">
        {/* Subtle background glow */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-32 bg-accent-primary/10 blur-3xl rounded-full"></div>
        
        <div className="relative z-10">
          <div className="text-center mb-8 flex flex-col items-center">
            <img src="/logo.png" alt="DVPL ERP" className="w-56 bg-white p-3 rounded-lg mb-6 object-contain shadow-lg" />
            <h1 className="text-3xl font-display font-bold text-text-primary tracking-wide">DVPL ERP</h1>
            <p className="text-text-secondary mt-2">Enterprise Construction Management System</p>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-status-danger/10 border border-status-danger/30 rounded text-status-danger text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-text-secondary mb-1">Username or Email</label>
              <input 
                type="text" 
                required
                className="input-field"
                placeholder="Enter Username or Email"
                value={username}
                onChange={(e) => setUsername(e.target.value.trim())}
              />
            </div>
            
            <div>
              <div className="flex justify-between items-center mb-1">
                <label className="block text-sm font-medium text-text-secondary">Password</label>
                <Link to="/forgot-password" className="text-xs font-medium text-accent-primary hover:text-text-primary transition-colors">
                  Forgot Password?
                </Link>
              </div>
              <input 
                type="password" 
                required
                className="input-field"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>

            <button 
              type="submit" 
              className="btn btn-primary w-full py-3 mt-4 text-base"
              disabled={loading}
            >
              {loading ? 'Authenticating...' : 'Sign In'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
